/**
 * Les tournées du planning (lot PL3, ADR-0015 ; interventions depuis ADR-0016) : la logique PURE de l'écran.
 *
 * Une tournée est « les mêmes machines d'un même site, tous les N mois » : l'écran recueille le site, les machines, la
 * nature, la fréquence, le premier jour, qui y va (ou personne : « À affecter »), puis le SERVEUR calcule les dates
 * (un samedi ou un dimanche glisse au lundi) et crée toutes les interventions d'un coup. Rien n'est recalculé ici : on formule
 * ce que le serveur rend, on ne le recompte pas.
 *
 * Convention n° 9 : la tournée ne recopie rien ; le client, le site, la gamme, la durée et le n° de commande se LISENT
 * (l'intervention dit « lu de la tournée » quand c'est le cas). Aucun montant nulle part.
 */
import type { Asset, EntreeTournee, ModificationTournee, Tournee, TourneeModifiee } from '../api/client';
import { lireDecimal, OPTIONS_QUANTITE } from './bon-edition';
import { dureePourSaisie, jourFr, jourValide, lireDuree, PLAFOND_COMMANDE, PLAFOND_DESCRIPTION } from './planning';

// --- La fréquence ----------------------------------------------------------------------------------

export type CleFrequence = 'MENSUELLE' | 'TRIMESTRIELLE' | 'SEMESTRIELLE' | 'ANNUELLE' | 'AUTRE';

export const FREQUENCES: readonly { readonly cle: CleFrequence; readonly mois: number | null; readonly libelle: string }[] = [
  { cle: 'MENSUELLE', mois: 1, libelle: 'Mensuelle' },
  { cle: 'TRIMESTRIELLE', mois: 3, libelle: 'Trimestrielle' },
  { cle: 'SEMESTRIELLE', mois: 6, libelle: 'Semestrielle' },
  { cle: 'ANNUELLE', mois: 12, libelle: 'Annuelle' },
  { cle: 'AUTRE', mois: null, libelle: 'Tous les N mois' },
];

export const MOIS_MIN = 1;
export const MOIS_MAX = 60;
export const PLAFOND_LIBELLE_TOURNEE = 120;
export const PLAFOND_REMARQUE = 1000;
export const MACHINES_MAX = 50;
export const TECHNICIENS_MAX = 20;

/** « chaque trimestre », « tous les 4 mois » : la fréquence dite en une locution. */
export function libelleFrequence(mois: number): string {
  switch (mois) {
    case 1:
      return 'chaque mois';
    case 3:
      return 'chaque trimestre';
    case 6:
      return 'chaque semestre';
    case 12:
      return 'chaque année';
    default:
      return `tous les ${mois} mois`;
  }
}

// --- Le formulaire de la nouvelle tournée ----------------------------------------------------------

/** Une pièce en plus demandée pour la PREMIÈRE visite, avec la machine qui la recevra. */
export interface PieceTournee {
  readonly assetId: string;
  readonly designation: string;
  readonly quantity: string;
  readonly reference?: string;
  readonly unit?: string;
}

export interface FormulaireTournee {
  readonly siteId: string | null;
  readonly siteLibelle: string | null;
  /** Les machines cochées (identifiants). Jamais plus de 50 : le serveur refuse au-delà. */
  readonly assetIds: readonly string[];
  readonly natureId: string;
  readonly frequence: CleFrequence;
  /** Le nombre de mois quand la fréquence est « tous les N mois ». */
  readonly moisTexte: string;
  readonly premierJour: string;
  /** Les techniciens habituels ; vide = « À affecter » : les visites ont une date et personne. */
  readonly technicienIds: readonly string[];
  readonly commande: string;
  readonly dureeTexte: string;
  /** La remarque PERMANENTE de la tournée : lue sur chaque visite. */
  readonly remarque: string;
  /** La remarque de la PREMIÈRE visite seulement (« ce mois-ci, regarder l'huile restante »). */
  readonly remarquePremiere: string;
  readonly pieces: readonly PieceTournee[];
}

export function formulaireTourneeVide(premierJour = ''): FormulaireTournee {
  return {
    siteId: null,
    siteLibelle: null,
    assetIds: [],
    natureId: '',
    frequence: 'TRIMESTRIELLE',
    moisTexte: '',
    premierJour,
    technicienIds: [],
    commande: '',
    dureeTexte: '',
    remarque: '',
    remarquePremiere: '',
    pieces: [],
  };
}

/** Le nombre de mois du formulaire ; `'ERREUR'` quand « tous les N mois » n'est pas un entier de 1 à 60. */
export function moisDuFormulaire(f: Pick<FormulaireTournee, 'frequence' | 'moisTexte'>): number | 'ERREUR' {
  const fixe = FREQUENCES.find((x) => x.cle === f.frequence)?.mois ?? null;
  if (fixe !== null) return fixe;
  const t = f.moisTexte.trim();
  if (!/^\d+$/.test(t)) return 'ERREUR';
  const n = Number(t);
  return n >= MOIS_MIN && n <= MOIS_MAX ? n : 'ERREUR';
}

/** Choisit un site : un autre site efface les machines, les pièces et le site précédent (les machines sont celles d'UN site). */
export function choisirSiteTournee(f: FormulaireTournee, site: { readonly id: string; readonly libelle: string }): FormulaireTournee {
  if (f.siteId === site.id) return f;
  return { ...f, siteId: site.id, siteLibelle: site.libelle, assetIds: [], pieces: [] };
}

export function oublierSiteTournee(f: FormulaireTournee): FormulaireTournee {
  return { ...f, siteId: null, siteLibelle: null, assetIds: [], pieces: [] };
}

/** Coche ou décoche une machine : une machine décochée perd les pièces en plus qu'on lui avait demandées. */
export function cocherMachine(f: FormulaireTournee, assetId: string, cochee: boolean): FormulaireTournee {
  if (cochee) return f.assetIds.includes(assetId) ? f : { ...f, assetIds: [...f.assetIds, assetId] };
  return { ...f, assetIds: f.assetIds.filter((id) => id !== assetId), pieces: f.pieces.filter((p) => p.assetId !== assetId) };
}

/**
 * Les machines cochées d'emblée quand on choisit un site : TOUTES celles qui ont une gamme (une tournée d'entretien se fait sur
 * ce qui a un entretien à faire) ; une machine sans gamme reste décochée et le formulaire le dit. L'ordre est celui de la liste.
 */
export function machinesCocheesDEmblee(machines: readonly { readonly assetId: string; readonly aUneGamme: boolean }[]): string[] {
  return machines.filter((m) => m.aUneGamme).map((m) => m.assetId).slice(0, MACHINES_MAX);
}

/**
 * Le choix des techniciens habituels : on bascule un technicien ; « À affecter » (aucun) vide la liste. La liste vide EST
 * « À affecter » : l'une ne peut donc jamais coexister avec des techniciens cochés.
 */
export function basculerTechnicienTournee(f: FormulaireTournee, technicienId: string): FormulaireTournee {
  return f.technicienIds.includes(technicienId)
    ? { ...f, technicienIds: f.technicienIds.filter((id) => id !== technicienId) }
    : { ...f, technicienIds: [...f.technicienIds, technicienId] };
}

export function choisirAAffecter(f: FormulaireTournee): FormulaireTournee {
  return { ...f, technicienIds: [] };
}

export function estAAffecter(f: Pick<FormulaireTournee, 'technicienIds'>): boolean {
  return f.technicienIds.length === 0;
}

/** Ajoute une pièce en plus pour la première visite. La machine doit être l'une des cochées. */
export function ajouterPieceTournee(f: FormulaireTournee, piece: PieceTournee): FormulaireTournee {
  if (!f.assetIds.includes(piece.assetId)) return f;
  return { ...f, pieces: [...f.pieces, piece] };
}

export function retirerPieceTournee(f: FormulaireTournee, index: number): FormulaireTournee {
  return { ...f, pieces: f.pieces.filter((_, i) => i !== index) };
}

/** Ce qu'une pièce en plus saisie donne : la quantité à la française lue en texte décimal, la désignation obligatoire. */
export type LecturePieceTournee = { readonly ok: true; readonly piece: Omit<PieceTournee, 'assetId'> } | { readonly ok: false; readonly erreur: string };

export function lirePieceTournee(saisie: { designation: string; quantite: string; reference: string; unite: string }): LecturePieceTournee {
  const designation = saisie.designation.trim();
  if (designation === '') return { ok: false, erreur: 'Dites quelle pièce (la désignation est obligatoire).' };
  if (designation.length > 200) return { ok: false, erreur: 'La désignation : 200 caractères au plus.' };
  const reference = saisie.reference.trim();
  if (reference.length > 80) return { ok: false, erreur: 'La référence : 80 caractères au plus.' };
  const unite = saisie.unite.trim();
  if (unite.length > 20) return { ok: false, erreur: 'L’unité : 20 caractères au plus.' };
  const quantite = lireDecimal(saisie.quantite, OPTIONS_QUANTITE);
  if (!quantite.ok) return { ok: false, erreur: `Quantité : ${quantite.erreur}` };
  if (quantite.vide) return { ok: false, erreur: 'Dites combien (la quantité est obligatoire).' };
  if (Number(quantite.valeur) <= 0) return { ok: false, erreur: 'La quantité doit être supérieure à zéro.' };
  return {
    ok: true,
    piece: { designation, quantity: quantite.valeur, ...(reference !== '' ? { reference } : {}), ...(unite !== '' ? { unit: unite } : {}) },
  };
}

/** Ce qui empêche de créer la tournée, dit en français ; liste vide = on peut. */
export function validerFormulaireTournee(f: FormulaireTournee): string[] {
  const m: string[] = [];
  if (f.siteId === null) m.push('Choisissez le site de la tournée.');
  if (f.assetIds.length === 0) m.push('Cochez au moins une machine.');
  if (f.assetIds.length > MACHINES_MAX) m.push(`Une tournée porte ${MACHINES_MAX} machines au plus.`);
  if (f.natureId === '') m.push('Choisissez la nature de la tournée (entretien, dépannage…).');
  if (moisDuFormulaire(f) === 'ERREUR') m.push(`La fréquence : un nombre de mois entier, de ${MOIS_MIN} à ${MOIS_MAX}.`);
  if (!jourValide(f.premierJour)) m.push('Choisissez le premier jour.');
  if (f.technicienIds.length > TECHNICIENS_MAX) m.push(`${TECHNICIENS_MAX} techniciens habituels au plus.`);
  if (lireDuree(f.dureeTexte) === 'ERREUR') m.push('La durée : de 15 minutes à 24 heures (« 8 », « 7,5 », « 1h30 », « 45 min »).');
  if (f.commande.trim().length > PLAFOND_COMMANDE) m.push(`Le n° de commande : ${PLAFOND_COMMANDE} caractères au plus.`);
  if (f.remarque.trim().length > PLAFOND_REMARQUE) m.push(`La remarque de la tournée : ${PLAFOND_REMARQUE} caractères au plus.`);
  if (f.remarquePremiere.trim().length > PLAFOND_DESCRIPTION) m.push(`La remarque de la première visite : ${PLAFOND_DESCRIPTION} caractères au plus.`);
  if (f.pieces.some((p) => !f.assetIds.includes(p.assetId))) m.push('Une pièce en plus vise une machine qui n’est plus cochée.');
  return m;
}

/** Le corps de la création. `cle` rend la création rejouable : un double clic ne crée pas deux tournées. */
export function versEntreeTournee(f: FormulaireTournee, cle: string): EntreeTournee {
  const mois = moisDuFormulaire(f);
  const duree = lireDuree(f.dureeTexte);
  const commande = f.commande.trim();
  const remarque = f.remarque.trim();
  const premiere = f.remarquePremiere.trim();
  return {
    clientKey: cle,
    siteId: f.siteId ?? '',
    natureId: f.natureId,
    frequencyMonths: mois === 'ERREUR' ? 0 : mois,
    firstDay: f.premierJour,
    technicianIds: [...f.technicienIds],
    assetIds: [...f.assetIds],
    ...(commande !== '' ? { customerOrderRef: commande } : {}),
    ...(remarque !== '' ? { note: remarque } : {}),
    ...(typeof duree === 'number' ? { durationMinutes: duree } : {}),
    ...(premiere !== '' ? { firstVisitNote: premiere } : {}),
    ...(f.pieces.length > 0
      ? {
          firstVisitParts: f.pieces.map((p) => ({
            assetId: p.assetId,
            designation: p.designation,
            quantity: p.quantity,
            ...(p.reference ? { reference: p.reference } : {}),
            ...(p.unit ? { unit: p.unit } : {}),
          })),
        }
      : {}),
  };
}

/** Le formulaire a-t-il été touché ? (fermer un volet touché demande confirmation). */
export function formulaireTourneeModifie(f: FormulaireTournee, depart: FormulaireTournee): boolean {
  return (
    f.siteId !== depart.siteId ||
    f.assetIds.length !== depart.assetIds.length ||
    f.natureId !== depart.natureId ||
    f.frequence !== depart.frequence ||
    f.moisTexte !== depart.moisTexte ||
    f.premierJour !== depart.premierJour ||
    f.technicienIds.length !== depart.technicienIds.length ||
    f.commande !== depart.commande ||
    f.dureeTexte !== depart.dureeTexte ||
    f.remarque !== depart.remarque ||
    f.remarquePremiere !== depart.remarquePremiere ||
    f.pieces.length !== depart.pieces.length
  );
}

// --- Modifier une tournée ----------------------------------------------------------------------------

/** Ce que le panneau « Modifier la tournée » montre : libellé, n° de commande, durée, remarque, machines et techniciens habituels (lot U4). */
export interface SaisieTournee {
  readonly label: string;
  readonly commande: string;
  readonly dureeTexte: string;
  readonly remarque: string;
  /** Les machines cochées (identifiants) : celles qu'on décoche sont retirées, celles qu'on coche sont ajoutées. */
  readonly assetIds: readonly string[];
  /** Les techniciens habituels cochés (identifiants). */
  readonly technicianIds: readonly string[];
  /** Coché : aucun déplacement facturé pour les interventions de la tournée (compris dans le forfait). */
  readonly sansDeplacement: boolean;
}

export function saisieDepuisTournee(
  t: Pick<Tournee, 'label' | 'customerOrderRef' | 'durationMinutes' | 'note' | 'assets' | 'technicians' | 'travelWaived'>,
): SaisieTournee {
  return {
    label: t.label,
    commande: t.customerOrderRef ?? '',
    dureeTexte: dureePourSaisie(t.durationMinutes),
    remarque: t.note ?? '',
    assetIds: t.assets.map((a) => a.assetId),
    technicianIds: t.technicians.map((x) => x.id),
    sansDeplacement: t.travelWaived === true,
  };
}

/** Le nom d'une machine à cocher : sa désignation, sinon celle du client, le modèle ou le numéro de série. */
export function nomMachineTournee(a: Pick<Asset, 'label' | 'clientDesignation' | 'model' | 'serialNumber'>): string {
  return a.label || a.clientDesignation || a.model || a.serialNumber || 'Machine';
}

/** Ce que la saisie change aux MACHINES : celles à ajouter et celles à retirer (jamais les deux pour une même). */
export function changementMachines(
  t: Pick<Tournee, 'assets'>,
  s: Pick<SaisieTournee, 'assetIds'>,
): { readonly ajouter: string[]; readonly retirer: string[] } {
  const avant = new Set(t.assets.map((a) => a.assetId));
  const apres = new Set(s.assetIds);
  return { ajouter: [...apres].filter((id) => !avant.has(id)), retirer: [...avant].filter((id) => !apres.has(id)) };
}

/** Vrai quand la liste des techniciens habituels cochés diffère de celle de la tournée (l'ordre ne compte pas). */
export function habituelsChanges(t: Pick<Tournee, 'technicians'>, s: Pick<SaisieTournee, 'technicianIds'>): boolean {
  const avant = new Set(t.technicians.map((x) => x.id));
  const apres = new Set(s.technicianIds);
  return avant.size !== apres.size || [...apres].some((id) => !avant.has(id));
}

/**
 * Ce que la composition choisie va faire, dit AVANT d'enregistrer : retirer annule les interventions à venir de la machine, ajouter en
 * crée une par occurrence à venir, les habituels arrivent sur les interventions à venir qui n'ont personne. `null` quand rien ne change.
 */
export function phraseEffetComposition(
  t: Pick<Tournee, 'assets' | 'technicians'>,
  s: Pick<SaisieTournee, 'assetIds' | 'technicianIds'>,
): string | null {
  const { ajouter, retirer } = changementMachines(t, s);
  const morceaux: string[] = [];
  if (retirer.length > 0) {
    morceaux.push(`Retirer ${retirer.length === 1 ? 'une machine' : `${retirer.length} machines`} annule ses interventions à venir (le passé reste).`);
  }
  if (ajouter.length > 0) {
    morceaux.push(`Ajouter ${ajouter.length === 1 ? 'une machine' : `${ajouter.length} machines`} crée une intervention par occurrence à venir.`);
  }
  if (habituelsChanges(t, s)) {
    morceaux.push('Les techniciens habituels arrivent sur les interventions à venir qui n’ont personne ; une intervention déjà affectée garde son équipe.');
  }
  return morceaux.length === 0 ? null : morceaux.join(' ');
}

const pluriel = (n: number, mot: string, accord = '') => `${n} ${mot}${n > 1 ? 's' : ''}${accord && n > 1 ? accord : ''}`;

/** Le compte rendu d'une modification : ce que le changement a fait aux occurrences à venir. */
export function phraseResultatModification(
  r: Pick<TourneeModifiee, 'machinesAjoutees' | 'machinesRetirees' | 'interventionsAnnulees' | 'interventionsCreees' | 'interventionsAffectees'>,
): string {
  const faits: string[] = [];
  if (r.interventionsAnnulees > 0) faits.push(`${pluriel(r.interventionsAnnulees, 'intervention')} à venir annulée${r.interventionsAnnulees > 1 ? 's' : ''}`);
  if (r.interventionsCreees > 0) faits.push(`${pluriel(r.interventionsCreees, 'intervention')} créée${r.interventionsCreees > 1 ? 's' : ''}`);
  if (r.interventionsAffectees > 0) {
    faits.push(`${pluriel(r.interventionsAffectees, 'intervention')} affectée${r.interventionsAffectees > 1 ? 's' : ''} aux techniciens habituels`);
  }
  if (faits.length > 0) return `Tournée modifiée : ${faits.join(', ')}.`;
  if (r.machinesAjoutees > 0 || r.machinesRetirees > 0) return 'Tournée modifiée : aucune occurrence à venir n’était concernée.';
  return 'Tournée modifiée : le n° de commande et la remarque sont lus par toutes ses interventions.';
}

/**
 * Le corps du `PATCH` : seulement ce qui a CHANGÉ (clé absente = inchangé, `null` efface). `corps: null` = rien à enregistrer.
 * Un libellé vide est refusé (une tournée garde un nom) ; les autres champs se vident.
 */
export function corpsModificationTournee(
  t: Pick<Tournee, 'label' | 'customerOrderRef' | 'durationMinutes' | 'note' | 'revision' | 'assets' | 'technicians' | 'travelWaived'>,
  s: SaisieTournee,
):
  | { readonly ok: true; readonly corps: ModificationTournee | null }
  | { readonly ok: false; readonly erreur: string } {
  const label = s.label.trim();
  if (label === '') return { ok: false, erreur: 'Donnez un nom à la tournée.' };
  if (label.length > PLAFOND_LIBELLE_TOURNEE) return { ok: false, erreur: `Le nom : ${PLAFOND_LIBELLE_TOURNEE} caractères au plus.` };
  const commande = s.commande.trim();
  if (commande.length > PLAFOND_COMMANDE) return { ok: false, erreur: `Le n° de commande : ${PLAFOND_COMMANDE} caractères au plus.` };
  const remarque = s.remarque.trim();
  if (remarque.length > PLAFOND_REMARQUE) return { ok: false, erreur: `La remarque : ${PLAFOND_REMARQUE} caractères au plus.` };
  const duree = lireDuree(s.dureeTexte);
  if (duree === 'ERREUR') return { ok: false, erreur: 'La durée : de 15 minutes à 24 heures (« 8 », « 7,5 », « 1h30 », « 45 min »).' };
  const corps: { -readonly [K in keyof ModificationTournee]: ModificationTournee[K] } = { revision: t.revision };
  let change = false;
  if (label !== t.label) {
    corps.label = label;
    change = true;
  }
  if (commande !== (t.customerOrderRef ?? '')) {
    corps.customerOrderRef = commande === '' ? null : commande;
    change = true;
  }
  if (duree !== t.durationMinutes) {
    corps.durationMinutes = duree;
    change = true;
  }
  if (remarque !== (t.note ?? '')) {
    corps.note = remarque === '' ? null : remarque;
    change = true;
  }
  if (s.assetIds.length === 0) return { ok: false, erreur: 'Une tournée garde au moins une machine : arrêtez-la plutôt que de la vider.' };
  if (s.assetIds.length > MACHINES_MAX) return { ok: false, erreur: `Une tournée porte ${MACHINES_MAX} machines au plus.` };
  const { ajouter, retirer } = changementMachines(t, s);
  if (ajouter.length > 0) {
    corps.addAssetIds = ajouter;
    change = true;
  }
  if (retirer.length > 0) {
    corps.removeAssetIds = retirer;
    change = true;
  }
  if (habituelsChanges(t, s)) {
    corps.technicianIds = [...s.technicianIds];
    change = true;
  }
  if (s.sansDeplacement !== (t.travelWaived === true)) {
    corps.travelWaived = s.sansDeplacement;
    change = true;
  }
  return { ok: true, corps: change ? corps : null };
}

// --- Dire ce qu'on voit ----------------------------------------------------------------------------

/** Le nombre d'interventions que la tournée crée : une par machine et par passage (au moins une machine comptée). */
export function nombreInterventions(passages: number, machines: number): number {
  return passages * Math.max(1, machines);
}

/**
 * « 4 passages × 2 machines = 8 interventions : 3 nov., 2 févr., … » ; avec une seule machine, « 4 interventions : … » : les dates
 * rendues par le serveur, à la française.
 */
export function phraseOccurrences(items: readonly { readonly no: number; readonly date: string }[], machines = 1): string {
  if (items.length === 0) return 'Aucune intervention à créer : vérifiez le premier jour et la fréquence.';
  const dates = items.map((o) => jourFr(o.date)).join(' · ');
  const total = nombreInterventions(items.length, machines);
  const interventions = total === 1 ? '1 intervention' : `${total} interventions`;
  if (machines <= 1) return `${interventions} : ${dates}`;
  const passages = items.length === 1 ? '1 passage' : `${items.length} passages`;
  return `${passages} × ${machines} machines = ${interventions} : ${dates}`;
}

/** Le texte du bouton de création : « Créer les 8 interventions » (passages × machines), « Créer l’intervention ». */
export function libelleCreation(passages: number | null, machines = 1): string {
  if (passages === null || passages === 0) return 'Créer la tournée';
  const nombre = nombreInterventions(passages, machines);
  return nombre === 1 ? 'Créer l’intervention' : `Créer les ${nombre} interventions`;
}

// --- Suite à donner → intervention -----------------------------------------------------------------------

/**
 * La nature proposée pour l'intervention d'une suite : celle du modèle « Entretien » pour une révision, du modèle « Dépannage »
 * pour un retour — lue sur le MODÈLE de feuille, pas sur un nom. Aucune nature de ce modèle : on ne devine rien (`null`).
 */
export function natureDeLaSuite(kind: string, natures: readonly { readonly id: string; readonly layout: string; readonly archived: boolean }[]): string | null {
  const modele = kind === 'REVISION_TO_PLAN' ? 'MAINTENANCE' : kind === 'RETURN_TO_PLAN' ? 'REPAIR' : null;
  if (modele === null) return null;
  return natures.find((n) => !n.archived && n.layout === modele)?.id ?? null;
}

/** Ce que le planning lit de l'adresse pour ouvrir le volet d'une intervention née d'une suite. */
export function lireDepuisSuite(params: URLSearchParams): { readonly suiteId: string; readonly machineId: string; readonly natureId: string | null } | null {
  const suiteId = params.get('depuisSuite');
  const machineId = params.get('machine');
  if (!suiteId || !machineId) return null;
  return { suiteId, machineId, natureId: params.get('nature') || null };
}
